import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { 
  MapPin, 
  LogOut, 
  Users, 
  UserPlus, 
  Edit, 
  Trash2, 
  ArrowLeft,
  Search,
  FileText,
  ShieldCheck,
  ShieldOff,
  Bell,
  BellOff,
  Send
} from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkersPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    surname: '',
    role: 'worker',
    daily_rate: 0,
    phone: ''
  });
  const [activeTab, setActiveTab] = useState('workers');
  const [notifSettings, setNotifSettings] = useState([]);
  const [isLoadingNotif, setIsLoadingNotif] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workers`, { withCredentials: true });
      setWorkers(response.data);
    } catch (error) {
      // silenced
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const fetchNotifSettings = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoadingNotif(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/notification-settings`, { withCredentials: true });
      setNotifSettings(response.data);
    } catch (error) {
      // silenced
    } finally {
      setIsLoadingNotif(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === 'notifications' && isAdmin) {
      fetchNotifSettings();
    }
  }, [activeTab, isAdmin, fetchNotifSettings]);

  const updateNotifSetting = async (userId, field, value) => {
    try {
      await axios.put(`${API_URL}/api/admin/notification-settings/${userId}`, {
        [field]: value
      }, { withCredentials: true });
      setNotifSettings(prev => prev.map(s =>
        s.user_id === userId ? { ...s, [field]: value } : s
      ));
    } catch (error) {
      // silenced
    }
  };

  const sendTestNotification = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/send-test-notification/${userId}`, {}, { withCredentials: true });
      alert('Notifica di test inviata!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore invio notifica');
    }
  };

  const handleCreateWorker = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/workers`, {
        ...formData,
        daily_rate: parseFloat(formData.daily_rate) || 0
      }, { withCredentials: true });
      setIsCreateOpen(false);
      setFormData({ email: '', password: '', name: '', surname: '', role: 'worker', daily_rate: 0, phone: '' });
      fetchWorkers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditWorker = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await axios.put(`${API_URL}/api/workers/${selectedWorker.user_id}`, {
        name: formData.name,
        surname: formData.surname,
        role: formData.role,
        daily_rate: parseFloat(formData.daily_rate) || 0,
        phone: formData.phone,
        password: formData.password || undefined
      }, { withCredentials: true });
      setIsEditOpen(false);
      setSelectedWorker(null);
      fetchWorkers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella modifica');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteWorker = async () => {
    setIsLoading(true);
    try {
      await axios.delete(`${API_URL}/api/workers/${selectedWorker.user_id}`, { withCredentials: true });
      setIsDeleteOpen(false);
      setSelectedWorker(null);
      fetchWorkers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella cancellazione');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAccess = async (workerId) => {
    try {
      await axios.post(`${API_URL}/api/workers/${workerId}/toggle-access`, {}, { withCredentials: true });
      fetchWorkers();
    } catch (error) {
      // silenced
    }
  };

  const openEditDialog = (worker) => {
    setSelectedWorker(worker);
    setFormData({
      email: worker.email,
      password: '',
      name: worker.name,
      surname: worker.surname,
      role: worker.role,
      daily_rate: worker.daily_rate || 0,
      phone: worker.phone || ''
    });
    setIsEditOpen(true);
  };

  const openDeleteDialog = (worker) => {
    setSelectedWorker(worker);
    setIsDeleteOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredWorkers = workers.filter(worker =>
    worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'bg-purple-100 text-purple-800',
      supervisor: 'bg-blue-100 text-blue-800',
      worker: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      admin: 'Admin',
      supervisor: 'Supervisore',
      worker: 'Operaio'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges[role]}`}>
        {labels[role]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E4E4E7] h-16 sm:h-20 flex items-center px-4 sm:px-8">
        <div className="flex items-center gap-3 flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mr-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-10 h-10 bg-[#0055FF] rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#09090B]">Gestione Operai</h1>
            <p className="text-xs text-[#71717A] hidden sm:block">
              {workers.length} utenti registrati
            </p>
          </div>
        </div>

        <nav className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/reports')}
            data-testid="nav-reports-btn"
            className="text-[#09090B] hover:bg-[#F4F4F5] hidden sm:flex"
          >
            <FileText className="w-4 h-4 mr-2" />
            Report
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="text-[#FF3B30] hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Esci</span>
          </Button>
        </nav>
      </header>

      {/* Main Content */}
      <main className="p-4 sm:p-8 max-w-6xl mx-auto">
        {/* Tab Selector */}
        {isAdmin && (
          <div className="flex gap-2 mb-6">
            <Button variant={activeTab === 'workers' ? 'default' : 'outline'} 
              onClick={() => setActiveTab('workers')}
              className={activeTab === 'workers' ? 'bg-[#0055FF]' : ''}
              data-testid="tab-workers">
              <Users className="w-4 h-4 mr-2" />Operai
            </Button>
            <Button variant={activeTab === 'notifications' ? 'default' : 'outline'}
              onClick={() => setActiveTab('notifications')}
              className={activeTab === 'notifications' ? 'bg-[#0055FF]' : ''}
              data-testid="tab-notifications">
              <Bell className="w-4 h-4 mr-2" />Notifiche Push
            </Button>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && isAdmin && (
          <Card className="border-[#E4E4E7] shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#0055FF]" />
                Gestione Notifiche Push
              </CardTitle>
              <p className="text-sm text-[#71717A] mt-1">
                Promemoria check-in alle 07:00 e check-out alle 15:30 (Lun-Ven). Gli operai devono attivare le notifiche dalla loro dashboard.
              </p>
            </CardHeader>
            <CardContent>
              {isLoadingNotif ? (
                <div className="text-center py-8 text-[#71717A]">Caricamento...</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Operaio</TableHead>
                        <TableHead className="text-center">Dispositivo</TableHead>
                        <TableHead className="text-center">Notifiche</TableHead>
                        <TableHead className="text-center">Check-in</TableHead>
                        <TableHead className="text-center">Check-out</TableHead>
                        <TableHead className="text-center">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notifSettings.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-[#71717A] py-8">
                            Nessun operaio trovato
                          </TableCell>
                        </TableRow>
                      ) : (
                        notifSettings.map((setting) => (
                          <TableRow key={setting.user_id} className={!setting.is_active ? 'opacity-50' : ''}>
                            <TableCell>
                              <div>
                                <p className="font-semibold text-[#09090B]">{setting.name} {setting.surname}</p>
                                <p className="text-xs text-[#71717A]">{setting.is_active ? 'Attivo' : 'Disattivato'}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {setting.has_subscription ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#34C759]/20 text-[#34C759]">
                                  <Bell className="w-3 h-3" /> Registrato
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#F4F4F5] text-[#71717A]">
                                  <BellOff className="w-3 h-3" /> Non registrato
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch
                                checked={setting.notifications_enabled}
                                onCheckedChange={(v) => updateNotifSetting(setting.user_id, 'notifications_enabled', v)}
                                data-testid={`notif-toggle-${setting.user_id}`}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="time"
                                value={setting.checkin_time}
                                onChange={(e) => updateNotifSetting(setting.user_id, 'checkin_time', e.target.value)}
                                className="w-24 mx-auto text-center text-sm"
                                data-testid={`checkin-time-${setting.user_id}`}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                <Switch
                                  checked={setting.checkout_reminder}
                                  onCheckedChange={(v) => updateNotifSetting(setting.user_id, 'checkout_reminder', v)}
                                  data-testid={`checkout-toggle-${setting.user_id}`}
                                />
                                {setting.checkout_reminder && (
                                  <Input
                                    type="time"
                                    value={setting.checkout_time}
                                    onChange={(e) => updateNotifSetting(setting.user_id, 'checkout_time', e.target.value)}
                                    className="w-24 text-center text-sm"
                                    data-testid={`checkout-time-${setting.user_id}`}
                                  />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendTestNotification(setting.user_id)}
                                disabled={!setting.has_subscription}
                                className="border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF]/10"
                                data-testid={`test-notif-${setting.user_id}`}
                              >
                                <Send className="w-3 h-3 mr-1" />Test
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Workers Tab */}
        {activeTab === 'workers' && (
        <Card className="border-[#E4E4E7] shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#0055FF]" />
                Lista Utenti
              </CardTitle>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                  <Input
                    placeholder="Cerca..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 border-[#E4E4E7] w-full sm:w-64"
                    data-testid="search-input"
                  />
                </div>
                {isAdmin && (
                  <Button
                    onClick={() => {
                      setFormData({ email: '', password: '', name: '', surname: '', role: 'worker', daily_rate: 0, phone: '' });
                      setIsCreateOpen(true);
                    }}
                    data-testid="add-worker-btn"
                    className="bg-[#0055FF] hover:bg-[#0040D0] text-white"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Aggiungi
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Ruolo</TableHead>
                    {isAdmin && <TableHead className="text-center">€/Giorno</TableHead>}
                    {isAdmin && <TableHead className="text-center">Accesso</TableHead>}
                    {isAdmin && <TableHead className="text-right">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorkers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 6 : 3} className="text-center text-[#71717A] py-8">
                        Nessun utente trovato
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredWorkers.map((worker, index) => (
                      <motion.tr
                        key={worker.user_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className={`hover:bg-[#F4F4F5] ${worker.is_active === false ? 'opacity-50' : ''}`}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {worker.is_active === false && (
                              <ShieldOff className="w-4 h-4 text-[#FF3B30]" />
                            )}
                            {worker.name} {worker.surname}
                          </div>
                        </TableCell>
                        <TableCell>{worker.email}</TableCell>
                        <TableCell>{getRoleBadge(worker.role)}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-center font-semibold text-[#0055FF]">
                            €{(worker.daily_rate || 0).toFixed(2)}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Switch
                                checked={worker.is_active !== false}
                                onCheckedChange={() => handleToggleAccess(worker.user_id)}
                                disabled={worker.user_id === user?.user_id}
                                data-testid={`toggle-access-${worker.user_id}`}
                              />
                              <span className={`text-xs ${worker.is_active !== false ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                                {worker.is_active !== false ? 'Attivo' : 'Disabilitato'}
                              </span>
                            </div>
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(worker)}
                                data-testid={`edit-worker-${worker.user_id}`}
                                className="text-[#0055FF] hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(worker)}
                                data-testid={`delete-worker-${worker.user_id}`}
                                className="text-[#FF3B30] hover:bg-red-50"
                                disabled={worker.user_id === user?.user_id}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </motion.tr>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuovo Utente</DialogTitle>
            <DialogDescription>
              Inserisci i dati del nuovo utente
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateWorker}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-md text-sm mb-4">
                {error}
              </div>
            )}
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-name">Nome</Label>
                  <Input
                    id="create-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="create-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-surname">Cognome</Label>
                  <Input
                    id="create-surname"
                    value={formData.surname}
                    onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                    required
                    data-testid="create-surname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-email">Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="create-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-password">Password</Label>
                <Input
                  id="create-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="create-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-role">Ruolo</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="create-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="worker">Operaio</SelectItem>
                    <SelectItem value="supervisor">Supervisore</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="create-daily-rate">Tariffa Giornaliera (€)</Label>
                  <Input
                    id="create-daily-rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.daily_rate}
                    onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                    placeholder="0.00"
                    data-testid="create-daily-rate-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="create-phone">Telefono (WhatsApp)</Label>
                <Input
                  id="create-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+39 333 1234567"
                  data-testid="create-phone-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
                data-testid="create-submit-btn"
                className="bg-[#0055FF] hover:bg-[#0040D0]"
              >
                {isLoading ? 'Creazione...' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Utente</DialogTitle>
            <DialogDescription>
              Modifica i dati dell'utente
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditWorker}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-md text-sm mb-4">
                {error}
              </div>
            )}
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Nome</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="edit-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surname">Cognome</Label>
                  <Input
                    id="edit-surname"
                    value={formData.surname}
                    onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                    required
                    data-testid="edit-surname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">Nuova Password (opzionale)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Lascia vuoto per non modificare"
                  data-testid="edit-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Ruolo</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="edit-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="worker">Operaio</SelectItem>
                    <SelectItem value="supervisor">Supervisore</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-daily-rate">Tariffa Giornaliera (€)</Label>
                <Input
                  id="edit-daily-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.daily_rate}
                  onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                  placeholder="0.00"
                  data-testid="edit-daily-rate-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Telefono (WhatsApp)</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+39 333 1234567"
                  data-testid="edit-phone-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
                data-testid="edit-submit-btn"
                className="bg-[#0055FF] hover:bg-[#0040D0]"
              >
                {isLoading ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare l'utente {selectedWorker?.name} {selectedWorker?.surname}?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Annulla
            </Button>
            <Button
              type="button"
              onClick={handleDeleteWorker}
              disabled={isLoading}
              data-testid="delete-confirm-btn"
              className="bg-[#FF3B30] hover:bg-[#E63329] text-white"
            >
              {isLoading ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkersPage;
